import { isEmpty } from '@festina-life/core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import type { Column, ColumnId } from './createColumnDefiner'

type Options<T> = {
  columns: Record<ColumnId, Column<T>>
}

type SortingState<T> = {
  cycleColumnSorting: (options: {
    columnId: ColumnId
    isAdditive: boolean
  }) => void
  getSortedRows: (rows: T[]) => T[]
  getSortingState: (columnId: ColumnId) => ColumnSortingState | null
}

export type ColumnSortingState = {
  desc: boolean
  /** Non-zero. Lower values sort first. `null` indicates priorities are not relevant as there is only one item being sorted. */
  priority: number | null
}

type ColumnSorting = {
  columnId: ColumnId
  state: Pick<ColumnSortingState, 'desc'>
}

const useComparator = () => {
  const intl = useIntl()
  const locale = intl.locale

  return useMemo(() => {
    const compare = (a: unknown, b: unknown): number => {
      if (isEmpty(a)) {
        return isEmpty(b) ? 0 : -1
      } else if (isEmpty(b)) {
        return 1
      }

      if (a instanceof Date && b instanceof Date) {
        return a.getTime() - b.getTime()
      }

      if (typeof a === 'string' && typeof b === 'string') {
        return a.localeCompare(b as string, locale, {
          numeric: true,
        })
      } else if (typeof a === 'number' && typeof b === 'number') {
        return a - b
      } else if (typeof a === 'bigint' && typeof b === 'bigint') {
        if (a < b) {
          return -1
        } else {
          return a > b ? 1 : 0
        }
      }

      if (typeof a === 'object' && typeof b === 'object') {
        const aValues = Array.isArray(a)
          ? a
          : Object.entries(a)
              .sort(([aaKey], [abKey]) => compare(aaKey, abKey))
              .map(([, aValue]) => aValue)
        const bValues = Array.isArray(b)
          ? b
          : Object.entries(b)
              .sort(([baKey], [bbKey]) => compare(baKey, bbKey))
              .map(([, bValue]) => bValue)

        const longest = Math.max(aValues.length, bValues.length)
        for (let i = 0; i < longest; i += 1) {
          const r = compare(aValues[i], bValues[i])
          if (r) {
            return r
          }
        }
        return 0
      }

      // No meaningful comparison. Best-effort by-type and then by-string representation.
      return `${typeof a} ${a}`.localeCompare(`${typeof b} ${b}`)
    }
    return compare
  }, [locale])
}

export const useSortingState = <T>({
  columns,
}: Options<T>): SortingState<T> => {
  const compare = useComparator()

  const [sortings, setSortings] = useState<ColumnSorting[]>(
    Object.entries(columns)
      .flatMap(([columnId, column]) =>
        column.sortOptions
          ? {
              columnId,
              state: { desc: column.sortOptions.sortType === 'desc' },
              priority: column.sortOptions.sortPriority ?? 0,
            }
          : [],
      )
      .sort((stateA, stateB) => stateA.priority - stateB.priority),
  )

  useEffect(
    () =>
      setSortings((sorting) =>
        sorting.filter(({ columnId }) => columnId in columns),
      ),
    [columns],
  )

  const getSortedRows = useCallback<SortingState<T>['getSortedRows']>(
    (rows) =>
      rows.toSorted((rowA, rowB) => {
        for (const { columnId, state } of sortings) {
          const column = columns[columnId]
          if (!column) {
            continue
          }
          const valueA = column.getSortValue(rowA)
          const valueB = column.getSortValue(rowB)
          const sortResult = compare(valueA, valueB)
          if (sortResult) {
            return sortResult * (state.desc ? -1 : 1)
          }
        }
        return 0
      }),
    [sortings, compare, columns],
  )

  const getSortingState = useCallback<SortingState<T>['getSortingState']>(
    (columnId) => {
      const index = sortings.findIndex(
        (sorting) => sorting.columnId === columnId,
      )
      if (index === -1) {
        return null
      }

      const { state } = sortings[index]
      return {
        ...state,
        priority: sortings.length === 1 ? null : index + 1,
      }
    },
    [sortings],
  )

  const cycleColumnSorting = useCallback<SortingState<T>['cycleColumnSorting']>(
    ({ columnId, isAdditive }) => {
      if (!(columnId in columns)) {
        return
      }

      setSortings((sortings) => {
        const currentState = sortings.find(
          (state) => state.columnId === columnId,
        )

        const newSorting = isAdditive
          ? sortings.filter((state) => state !== currentState)
          : []

        if (!currentState) {
          newSorting.push({ columnId, state: { desc: false } })
        } else if (!currentState.state.desc) {
          newSorting.push({ columnId, state: { desc: true } })
        } // else it cycles back to no sorting

        return newSorting
      })
    },
    [columns],
  )

  return {
    getSortedRows,
    getSortingState,
    cycleColumnSorting,
  }
}
